import React, { useState, useEffect, useCallback } from "react";
import {
  adminApiService,
  WeeklyRewardDistribution,
  WeeklyRewardType,
} from "../../services/adminApi";
import { useAdmin } from "../../hooks/useAdmin";
import { ApiError } from "../../services/api";
import styles from "../../styles/RewardsManagement.module.css";
import { appKitBlockchainService } from "../../services/appkitBlockchain";

const RewardsManagement: React.FC = () => {
  const { isAdmin } = useAdmin();
  const [activeSection, setActiveSection] = useState<"pending" | "history" | "calculate">("pending");
  const [pendingRewards, setPendingRewards] = useState<WeeklyRewardDistribution[]>([]);
  const [historyRewards, setHistoryRewards] = useState<WeeklyRewardDistribution[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [telegramMessage, setTelegramMessage] = useState<string | null>(null);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    }
  };

  const fetchPendingRewards = useCallback(async () => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      clearMessages();

      console.log("🔍 Fetching pending rewards...");
      const data = await adminApiService.getPendingRewardDistributions();
      console.log("📦 API Response:", data);
      console.log("📋 Distributions array:", data.distributions);
      console.log("📊 Array length:", data.distributions?.length || 0);

      setPendingRewards(data.distributions || []);
    } catch (err) {
      console.error("❌ Error fetching pending rewards:", err);
      const errorMessage = err instanceof ApiError ? err.message : "Failed to fetch pending rewards";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchRewardHistory = useCallback(
    async (weekStartDate: string) => {
      if (!isAdmin) return;

      try {
        setLoading(true);
        clearMessages();

        const data = await adminApiService.getWeeklyRewardDistribution(weekStartDate);
        setHistoryRewards(data ? [data] : []);
      } catch (err) {
        const errorMessage = err instanceof ApiError ? err.message : "Failed to fetch reward history";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [isAdmin]
  );

  const calculateRewards = async (weekStartDate: string) => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      clearMessages();

      const data = await adminApiService.calculateWeeklyRewards(weekStartDate);
      setSuccess(data.message || `Rewards calculated successfully for week ${weekStartDate}`);

      // Refresh pending rewards list
      await fetchPendingRewards();
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : "Failed to calculate rewards";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const executeAllRewards = async (weekStartDate: string) => {
    if (!isAdmin) return;

    if (
      !window.confirm(
        `Are you sure you want to execute ALL rewards for week ${weekStartDate}? This will trigger blockchain transactions.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      clearMessages();
      setTelegramMessage(null);

      const data = await adminApiService.executeAllWeeklyRewards(weekStartDate);

      if (data.success) {
        setSuccess(
          `All rewards executed successfully for week ${weekStartDate}. Executed types: ${data.executedTypes.join(
            ", "
          )}`
        );

        // Fetch the updated distribution to get transaction hashes and wallet addresses
        const distribution = await adminApiService.getWeeklyRewardDistribution(weekStartDate);

        if (distribution) {
          // Generate Telegram message
          const formatWeekDate = (dateStr: string) => {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          };

          const formatWallet = (wallet: string) => {
            return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
          };

          // Get top 3 for each category
          const gameCountTop3 = distribution.gameCountBnbRewards.slice(0, 3);
          const dxfMiningTop3 = distribution.dxfMiningBnbRewards.slice(0, 3);

          // Build winners sections
          const gameCountSection = gameCountTop3.length > 0
            ? gameCountTop3.map((r, i) => `#${i + 1} ${formatWallet(r.walletAddress)}`).join('\n')
            : 'No winners';

          const dxfMiningSection = dxfMiningTop3.length > 0
            ? dxfMiningTop3.map((r, i) => `#${i + 1} ${formatWallet(r.walletAddress)}`).join('\n')
            : 'No winners';

          // Build transaction links
          const txLinks: string[] = [];
          if (distribution.txHashes.gameCountBnb) {
            txLinks.push(`- BNB for Games Count Champions: ${appKitBlockchainService.explorerTx(distribution.txHashes.gameCountBnb)}`);
          }
          if (distribution.txHashes.gameCountDxf) {
            txLinks.push(`- DXF for Games Count Champions: ${appKitBlockchainService.explorerTx(distribution.txHashes.gameCountDxf)}`);
          }
          if (distribution.txHashes.dxfMiningBnb) {
            txLinks.push(`- BNB for DXF Mining Masters: ${appKitBlockchainService.explorerTx(distribution.txHashes.dxfMiningBnb)}`);
          }
          if (distribution.txHashes.dxfMiningDxf) {
            txLinks.push(`- DXF for DXF Mining Masters: ${appKitBlockchainService.explorerTx(distribution.txHashes.dxfMiningDxf)}`);
          }

          // Calculate next week date
          const currentWeekDate = new Date(weekStartDate);
          const nextWeekDate = new Date(currentWeekDate);
          nextWeekDate.setDate(currentWeekDate.getDate() + 7);
          const nextWeekFormatted = formatWeekDate(nextWeekDate.toISOString());

          const message = `**🏆 WEEK ${formatWeekDate(weekStartDate)} LEADERBOARD WINNERS!**

**🥇 Games Count Champions:**
${gameCountSection}

**⛏️ DXF Mining Masters:**
${dxfMiningSection}

**💰 Prizes distributed**
${txLinks.join('\n')}

Congratulations to all winners! 🎉

**🔥 NEW WEEK = FRESH COMPETITION!**
Week ${nextWeekFormatted} leaderboard is now LIVE with another prize pool!

Ready to dominate? LFG! 🚀
https://deathx.fun

#Winners #NewWeek #LeaderboardLive`;

          setTelegramMessage(message);
        }
      } else {
        setError(`Partial execution for week ${weekStartDate}. Errors: ${data.errors.join(", ")}`);
      }

      // Refresh pending rewards list
      await fetchPendingRewards();
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : "Failed to execute rewards";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const executeSpecificRewards = async (weekStartDate: string, rewardTypes: WeeklyRewardType[]) => {
    if (!isAdmin) return;

    if (
      !window.confirm(`Are you sure you want to execute ${rewardTypes.join(", ")} rewards for week ${weekStartDate}?`)
    ) {
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      const data = await adminApiService.executeSpecificWeeklyRewards(weekStartDate, rewardTypes);

      if (data.success) {
        setSuccess(
          `Specific rewards executed successfully for week ${weekStartDate}. Executed: ${data.executedTypes.join(", ")}`
        );
      } else {
        setError(`Partial execution for week ${weekStartDate}. Errors: ${data.errors.join(", ")}`);
      }

      // Refresh pending rewards list
      await fetchPendingRewards();
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : "Failed to execute specific rewards";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === "pending" && isAdmin) {
      fetchPendingRewards();
    }
  }, [activeSection, isAdmin, fetchPendingRewards]);

  // Don't render if not admin
  if (!isAdmin) {
    return (
      <div className={styles.rewardsManagement}>
        <div className={styles.rewardsHeader}>
          <h2>⚠️ Access Denied</h2>
          <p>Admin privileges required to manage rewards.</p>
        </div>
      </div>
    );
  }

  const formatAmount = (amount: string, decimals: number = 18) => {
    const num = parseFloat(amount) / Math.pow(10, decimals);
    return num.toFixed(decimals === 18 ? 6 : 4);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const getRewardTypeLabel = (type: WeeklyRewardType) => {
    const labels: Record<WeeklyRewardType, string> = {
      gameCountBnb: "Game Count (BNB)",
      gameCountDxf: "Game Count (DXF)",
      dxfMiningBnb: "DXF Mining (BNB)",
      dxfMiningDxf: "DXF Mining (DXF)",
    };
    return labels[type];
  };

  return (
    <div className={styles.rewardsManagement}>
      <div className={styles.rewardsHeader}>
        <h2>🎁 Weekly Rewards Management</h2>
        <p>Manage weekly leaderboard reward calculations and distributions</p>
      </div>

      {/* Messages */}
      {error && (
        <div className={styles.errorMessage}>
          ❌ {error}
          <button onClick={clearMessages} className={styles.closeButton}>
            ×
          </button>
        </div>
      )}

      {success && (
        <div className={styles.successMessage}>
          ✅ {success}
          <button onClick={clearMessages} className={styles.closeButton}>
            ×
          </button>
        </div>
      )}

      {/* Telegram Message */}
      {telegramMessage && (
        <div className={styles.telegramMessage}>
          <div className={styles.telegramHeader}>
            <h3>📱 Telegram Announcement Message</h3>
            <button
              onClick={async () => {
                const copied = await copyToClipboard(telegramMessage);
                if (copied) {
                  setSuccess('Telegram message copied to clipboard!');
                  setTimeout(() => setSuccess(null), 3000);
                }
              }}
              className={styles.copyButton}
            >
              📋 Copy Message
            </button>
          </div>
          <p className={styles.telegramInstructions}>Copy and send this message to the Telegram community group:</p>
          <div className={styles.messageContainer}>
            <pre className={styles.messageContent}>{telegramMessage}</pre>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className={styles.rewardsNavigation}>
        <button
          onClick={() => setActiveSection("pending")}
          className={`${styles.navButton} ${activeSection === "pending" ? styles.active : ""}`}
        >
          📋 Pending Rewards
        </button>
        <button
          onClick={() => setActiveSection("calculate")}
          className={`${styles.navButton} ${activeSection === "calculate" ? styles.active : ""}`}
        >
          🧮 Calculate Rewards
        </button>
        <button
          onClick={() => setActiveSection("history")}
          className={`${styles.navButton} ${activeSection === "history" ? styles.active : ""}`}
        >
          📊 History
        </button>
      </div>

      {/* Loading */}
      {loading && <div className={styles.loadingMessage}>⏳ Loading...</div>}

      {/* Content */}
      <div className={styles.rewardsContent}>
        {/* Pending Rewards Section */}
        {activeSection === "pending" && (
          <div className={styles.pendingSection}>
            {pendingRewards.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No pending reward distributions found.</p>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
                  Debug: Found {pendingRewards.length} pending reward distribution(s)
                </p>
                <div className={styles.rewardsGrid}>
                  {pendingRewards.map((distribution) => (
                    <div key={distribution.id} className={styles.rewardCard}>
                      <div className={styles.cardHeader}>
                        <h4>Week of {formatDate(distribution.weekStartDate)}</h4>
                        <span className={styles.calculatedDate}>
                          Calculated: {formatDate(distribution.calculatedAt)}
                        </span>
                      </div>

                      <div className={styles.cardStats}>
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Total BNB:</span>
                          <span className={styles.statValue}>{formatAmount(distribution.totalBnbAmount)} BNB</span>
                        </div>
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Total DXF:</span>
                          <span className={styles.statValue}>{formatAmount(distribution.totalDxfAmount)} DXF</span>
                        </div>
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Recipients:</span>
                          <span className={styles.statValue}>{distribution.totalRecipients}</span>
                        </div>
                      </div>

                      <div className={styles.executionStatus}>
                        <h5>Execution Status:</h5>
                        <div className={styles.statusGrid}>
                          {(["gameCountBnb", "gameCountDxf", "dxfMiningBnb", "dxfMiningDxf"] as WeeklyRewardType[]).map(
                            (type) => {
                              const executed = !!distribution.txHashes[type];
                              const hasRewards = type.includes("Bnb")
                                ? type === "gameCountBnb"
                                  ? distribution.gameCountBnbRewards.length > 0
                                  : distribution.dxfMiningBnbRewards.length > 0
                                : type === "gameCountDxf"
                                ? distribution.gameCountDxfRewards.length > 0
                                : distribution.dxfMiningDxfRewards.length > 0;

                              return (
                                <div key={type} className={styles.statusItem}>
                                  <span className={styles.statusLabel}>{getRewardTypeLabel(type)}:</span>
                                  <span
                                    className={`${styles.statusIndicator} ${
                                      executed ? styles.executed : hasRewards ? styles.pending : styles.noRewards
                                    }`}
                                  >
                                    {executed ? "✅ Executed" : hasRewards ? "⏳ Pending" : "ℹ️ No rewards"}
                                  </span>
                                  {executed && distribution.txHashes[type] && (
                                    <a
                                      href={appKitBlockchainService.explorerTx(distribution.txHashes[type]!)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={styles.txLink}
                                    >
                                      📋 View Tx
                                    </a>
                                  )}
                                  {!executed && hasRewards && (
                                    <button
                                      onClick={() => executeSpecificRewards(distribution.weekStartDate, [type])}
                                      className={styles.executeButton}
                                      disabled={loading}
                                    >
                                      ⚡ Execute
                                    </button>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>

                      <div className={styles.cardActions}>
                        <button
                          onClick={() => executeAllRewards(distribution.weekStartDate)}
                          className={styles.executeAllButton}
                          disabled={loading}
                        >
                          🚀 Execute All Pending
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Calculate Rewards Section */}
        {activeSection === "calculate" && (
          <div className={styles.calculateSection}>
            <div className={styles.calculateForm}>
              <div className={styles.formGroup}>
                <label htmlFor="weekSelect">Select Week (Monday date):</label>
                <input
                  type="date"
                  id="weekSelect"
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className={styles.dateInput}
                />
              </div>

              <button
                onClick={() => selectedWeek && calculateRewards(selectedWeek)}
                className={styles.calculateButton}
                disabled={!selectedWeek || loading}
              >
                🧮 Calculate Rewards
              </button>

              <div className={styles.calculateInfo}>
                <h4>ℹ️ Instructions:</h4>
                <ul>
                  <li>Select the Monday date of the week you want to calculate rewards for</li>
                  <li>Rewards can only be calculated for completed (inactive) leaderboards</li>
                  <li>This will generate reward distributions based on the active rewards configuration</li>
                  <li>After calculation, rewards will appear in the "Pending Rewards" section</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* History Section */}
        {activeSection === "history" && (
          <div className={styles.historySection}>
            <div className={styles.historyForm}>
              <div className={styles.formGroup}>
                <label htmlFor="historyWeekSelect">Select Week to View:</label>
                <input
                  type="date"
                  id="historyWeekSelect"
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className={styles.dateInput}
                />
              </div>

              <button
                onClick={() => selectedWeek && fetchRewardHistory(selectedWeek)}
                className={styles.searchButton}
                disabled={!selectedWeek || loading}
              >
                🔍 Search
              </button>
            </div>

            {historyRewards.length > 0 && (
              <div className={styles.historyResults}>
                {historyRewards.map((distribution) => (
                  <div key={distribution.id} className={styles.historyCard}>
                    <div className={styles.cardHeader}>
                      <h4>Week of {formatDate(distribution.weekStartDate)}</h4>
                      <span className={styles.calculatedDate}>Calculated: {formatDate(distribution.calculatedAt)}</span>
                    </div>

                    <div className={styles.cardStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total BNB:</span>
                        <span className={styles.statValue}>{formatAmount(distribution.totalBnbAmount)} BNB</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total DXF:</span>
                        <span className={styles.statValue}>{formatAmount(distribution.totalDxfAmount)} DXF</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Recipients:</span>
                        <span className={styles.statValue}>{distribution.totalRecipients}</span>
                      </div>
                    </div>

                    <div className={styles.executionHistory}>
                      <h5>Execution History:</h5>
                      <div className={styles.txList}>
                        {Object.entries(distribution.txHashes).map(
                          ([type, txHash]) =>
                            txHash && (
                              <div key={type} className={styles.txItem}>
                                <span className={styles.txType}>{getRewardTypeLabel(type as WeeklyRewardType)}:</span>
                                <a
                                  href={appKitBlockchainService.explorerTx(txHash)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.txHashLink}
                                >
                                  {txHash.slice(0, 10)}...{txHash.slice(-8)}
                                </a>
                              </div>
                            )
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedWeek && historyRewards.length === 0 && !loading && (
              <div className={styles.emptyState}>
                <p>No reward distribution found for the selected week.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardsManagement;
